import { Button, ChevronLeftIcon, ChevronRightIcon, Flex, toaster } from 'components'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { DraftSession, RegistrationConsentItem } from 'types'
import { useLocalStorage } from 'usehooks-ts'
import { createLeadSessions, forceLight, verifyReCaptchaToken } from 'utils'
import {
  AdditionalQuestionsSection,
  BasicQuestionsSection,
  DisclaimerSection,
  EventHeader,
  OptInSection,
  ProgressIndicator,
  ReCaptchaInfo,
  RegionSection,
} from '../components'
import EventLoading from '../components/EventLoading'
import RegistrationCompleteTile from '../components/RegistrationCompleteTile'
import {
  defaultFormSections,
  eventIsCurrent,
  FormSectionData,
  getConsentDerivedValues,
  getSectionHeader,
  useEvent,
} from '../utils'

const { VITE_PUBLIC_RECAPTCHA_SITE_KEY } = import.meta.env

export default function EventPage() {
  const event = useEvent()
  const usesRegionConsent = event?.consents?.length! > 1
  const radioColorScheme = 'yellow'
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [currentSection, setCurrentSection] = useState<number>(0)
  const [alreadySubmitted, setAlreadySubmitted] = useLocalStorage<boolean>(
    `submitted-event-${event?.id}`,
    false
  )
  const [userSession, setUserSession] = useState<DraftSession>({})
  const [skipBasicFields, setSkipBasicFields] = useState<boolean>()
  const [disclaimerAnswered, setDisclaimerAnswered] = useState<boolean>(false)
  const [additionalQuestionsAnswered, setAdditionalQuestionsAnswered] = useState<boolean>(false)
  const [resideInDefault, setResideInDefault] = useState<'no' | 'yes' | ''>('')
  const [submissionLoading, setSubmissionLoading] = useState<boolean>()

  useEffect(() => {
    if (!event?.leadDefaults || userSession?.lead) return
    setUserSession({ lead: event?.leadDefaults })
  }, [event])

  const {
    consentByRegion,
    disclaimerFromConsent,
    duringRegistrationConsentInfo,
    optInFromConsent,
    optInItems,
  } = useMemo(
    () =>
      getConsentDerivedValues(
        event?.template?.registration?.consents,
        event?.consents,
        resideInDefault
      ),
    [event?.template?.registration?.consents, event?.consents, resideInDefault]
  )

  const isCurrentEvent = eventIsCurrent(event?.startTime!, event?.endTime)
  const templateForm = event?.template.registration
  const templateFormSections: FormSectionData = {
    region: usesRegionConsent ? 'default' : undefined,
    disclaimer: disclaimerFromConsent,
    basicFields: templateForm?.fields,
    additionalFields: event?.template.traitMap,
    optIn: optInFromConsent,
  }
  const templateSections = Object.entries(templateFormSections)
    .filter(([_, v]) => v && (Array.isArray(v) ? v.length : Object.keys(v).length))
    .map(([k]) => k)
  const formSections = defaultFormSections.filter(section => templateSections.includes(section))
  const templateFormSectionsCount = formSections.length
  const additionalQuesRoute = event?.template?.routing

  useEffect(() => {
    const { lead, ...session } = userSession
    if (skipBasicFields) setUserSession(session)
  }, [skipBasicFields])

  useEffect(() => {
    if (alreadySubmitted) {
      setCurrentSection(Object.keys(formSections).length)
    }
  }, [alreadySubmitted])

  function handleMove({ back }: { back: boolean }) {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    const checkCondition = (i: number) => (back ? i < currentSection : i > currentSection)
    if (skipBasicFields) {
      const nextIndex = formSections.findIndex(
        (s, i) => checkCondition(i) && !['basicFields', 'optIn'].includes(s)
      )
      if (nextIndex !== -1) setCurrentSection(nextIndex)
    } else back ? setCurrentSection(currentSection - 1) : setCurrentSection(currentSection + 1)
  }

  async function handleSubmit() {
    try {
      if (!event) return
      const { id: eventId, momentId, workspaceId } = event
      await createLeadSessions([
        {
          ...userSession,
          appName: 'self-registration',
          deviceName: 'Self Registration',
          eventId,
          momentId,
          workspaceId,
        },
      ])
      setAlreadySubmitted(true)
      handleMove({ back: false })
    } catch (error: any) {
      toaster.create({
        title:
          'Submission failed, check network connection and try again. Talk to a company representative if issue persists.',
        type: 'error',
      })
    }
  }

  const reCaptchaAndSubmit = async () => {
    try {
      setSubmissionLoading(true)
      if (!recaptchaRef.current) {
        alert('reCAPTCHA not ready. Please reload the page.')
        return
      }
      const token = await recaptchaRef.current.executeAsync?.()
      if (!token) {
        alert('reCAPTCHA failed. Please try again.')
        return
      }
      const tokenVerified = await verifyReCaptchaToken({ token })
      if (tokenVerified.data) await handleSubmit()
      setSubmissionLoading(false)
      recaptchaRef.current.reset()
    } catch (err) {
      alert('Something went wrong with reCAPTCHA. Please try again.')
    }
  }

  if (!event) return <EventLoading />
  if (!VITE_PUBLIC_RECAPTCHA_SITE_KEY) return

  return (
    <Flex
      {...forceLight}
      ref={scrollContainerRef}
      direction="column"
      overflowY="auto"
      overflowX="hidden"
    >
      <EventHeader
        event={event}
        sectionLabel={isCurrentEvent ? getSectionHeader(formSections[currentSection]) : ''}
      />
      {currentSection === Object.keys(formSections).length ? (
        <RegistrationCompleteTile userSession={userSession} />
      ) : (
        <Flex alignItems="stretch" direction="column">
          {sectionRenderer()}
          <ProgressIndicator
            totalSections={templateFormSectionsCount}
            currentSection={currentSection}
            onClick={sectionClicked => setCurrentSection(sectionClicked)}
            blockedSections={formSections
              .map((section, idx) => (!isSectionComplete(section) ? idx : null))
              .filter((idx): idx is number => idx !== null)}
          />
          <Flex mx={4} justify="center">
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={VITE_PUBLIC_RECAPTCHA_SITE_KEY}
            />
          </Flex>
          <Flex gap={2} m={4}>
            {currentSection > 0 && (
              <Button
                flex={1}
                bg="white"
                border="1px solid rgba(255, 204, 0, 0.3)"
                leftIcon={<ChevronLeftIcon size="sm" />}
                onClick={() => handleMove({ back: true })}
              >
                Back
              </Button>
            )}
            <Button
              flex={1}
              alignSelf="stretch"
              bg="branding.catYellow"
              rightIcon={
                currentSection === templateFormSectionsCount - 1 ? undefined : (
                  <ChevronRightIcon size="sm" />
                )
              }
              loading={submissionLoading}
              loadingText="Submitting..."
              onClick={() =>
                currentSection === templateFormSectionsCount - 1
                  ? reCaptchaAndSubmit()
                  : handleMove({ back: false })
              }
              disabled={
                !isSectionComplete() ||
                (currentSection === templateFormSectionsCount - 1 && !isCurrentEvent)
              }
            >
              {currentSection === templateFormSectionsCount - 1 ? 'Done' : 'Next'}
            </Button>
          </Flex>
          <ReCaptchaInfo />
        </Flex>
      )}
    </Flex>
  )

  function sectionRenderer(): ReactNode {
    const sectionName = formSections[currentSection]
    const { disclaimer, basicFields, optIn } = templateFormSections
    const activeDisclaimerText = disclaimer?.text?.en

    switch (sectionName) {
      case 'region':
        return (
          <RegionSection
            colorScheme={radioColorScheme}
            onChange={value => setResideInDefault(value as 'no' | 'yes' | '')}
            previewMode={!isCurrentEvent}
            value={resideInDefault}
          />
        )

      case 'disclaimer':
        if (!activeDisclaimerText) return
        return (
          <DisclaimerSection
            colorScheme={radioColorScheme}
            onChange={value => {
              setSkipBasicFields(value === 'no' ? true : false)
              setDisclaimerAnswered(true)
            }}
            previewMode={!isCurrentEvent}
            text={activeDisclaimerText}
            value={disclaimerAnswered ? (skipBasicFields ? 'no' : 'yes') : ''}
          />
        )

      case 'basicFields':
        if (!basicFields) return
        return (
          <BasicQuestionsSection
            previewMode={!isCurrentEvent}
            basicFields={basicFields}
            userSession={userSession}
            registrationConsentInfo={duringRegistrationConsentInfo}
            setUserSession={setUserSession}
          />
        )

      case 'additionalFields':
        return (
          <AdditionalQuestionsSection
            routing={additionalQuesRoute}
            previewMode={!isCurrentEvent}
            colorScheme={radioColorScheme}
            templateFormSections={templateFormSections}
            userSession={userSession}
            setUserSession={setUserSession}
            setAdditionalQuestionsAnswered={setAdditionalQuestionsAnswered}
          />
        )

      case 'optIn':
        if (!optIn) return
        return (
          <OptInSection
            checked={!!userSession.lead?.optIn}
            colorScheme={radioColorScheme}
            content={optIn}
            consents={userSession.lead?.consents}
            onConsentsChange={consents =>
              setUserSession({
                ...userSession,
                lead: { ...userSession?.lead, consents },
              })
            }
            onChange={() =>
              setUserSession({
                ...userSession,
                lead: {
                  ...userSession?.lead,
                  optIn: !userSession?.lead?.optIn,
                },
              })
            }
            previewMode={!isCurrentEvent}
          />
        )
    }
  }

  function isSectionComplete(section?: string): boolean {
    const sectionName = section || formSections[currentSection]

    switch (sectionName) {
      case 'region':
        return resideInDefault === 'yes' || resideInDefault === 'no'
      case 'disclaimer':
        return disclaimerAnswered
      case 'basicFields':
        if (!templateFormSections.basicFields) return true
        return templateFormSections.basicFields.every(
          f =>
            !f.required ||
            Object.keys(userSession?.lead || {}).includes(f.property) ||
            Object.keys(userSession?.lead?.custom || {}).includes(f.property)
        )
      case 'additionalFields':
        return additionalQuestionsAnswered
      case 'optIn':
        if (!optInItems) return !!userSession.lead?.optIn
        const leadConsents = userSession.lead?.consents ?? []
        return optInItems.every((item: RegistrationConsentItem, index: number) => {
          const hasNoOption = !!(item.noText?.en ?? item.noText)
          const title = item.title?.en ?? `Consent ${index + 1}`
          const consent = leadConsents.find(c => c.title === title)
          if (!hasNoOption) return true
          if (consent?.accepted === null || consent?.accepted === undefined) return false
          if (consent.accepted === true && (item.subitems?.length ?? 0) > 0) {
            const acceptedSub = consent.acceptedSubItems ?? []
            return acceptedSub.length > 0
          }
          return true
        })

      default:
        return true
    }
  }
}
